import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import './LanguageSwitcher.css';

// Eigen knop + opklapmenu i.p.v. een native <select>: op vraag van de klant
// toont de dichtgeklapte staat enkel de kale afkorting (NL/FR/EN, geen
// vlaggetje) - het vlagemoji verschijnt pas naast elke optie zodra je het
// menu effectief opent. Een native <select> kan dat onderscheid niet maken
// (die toont altijd de volledige geselecteerde option-tekst, ook
// dichtgeklapt), vandaar deze eigen knop/lijst-opbouw met een
// buiten-klik-luisteraar om het menu weer te sluiten.
const LANGUAGES = [
  { code: 'nl', name: 'Nederlands', label: 'NL' },
  { code: 'fr', name: 'Français', label: 'FR' },
  { code: 'en', name: 'English', label: 'EN' },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = i18n.language?.startsWith('fr') ? 'fr' : i18n.language?.startsWith('en') ? 'en' : 'nl';
  const [open, setOpen] = useState(false);
  // Positie van het opklapmenu, berekend t.o.v. de knop zelf (viewport-
  // coördinaten) i.p.v. gewoon "position: absolute" binnen deze component.
  // Nodig omdat de knop in het mobiele hamburgermenu binnen een
  // "overflow-y: auto"-paneel (.navbar-menu.open) staat: een gewoon
  // absoluut gepositioneerd menu kan daar afgesneden worden of ergens
  // anders lijken op te poppen i.p.v. netjes onder de knop zelf.
  const [menuPos, setMenuPos] = useState(null);
  const containerRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sluit het menu bij scrollen (bv. het scrollbare hamburgermenu op
  // mobiel) i.p.v. het los van de knop te laten "blijven hangen" op zijn
  // oude, nu foute positie.
  useEffect(() => {
    if (!open) return undefined;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  const currentLabel = LANGUAGES.find((l) => l.code === current)?.label || 'NL';

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        // Menu-breedte is nog niet gekend (staat pas in de DOM na deze
        // render), dus schatten we ze op basis van de CSS min-width + wat
        // marge. Standaard rechts uitgelijnd met de knop (zoals voorheen),
        // maar geklemd binnen de viewport: op mobiel, waar de knop dicht bij
        // de linkerrand staat, zou rechts-uitlijnen het menu deels links van
        // het scherm laten uitsteken - dan lijnen we in plaats daarvan links
        // uit met de knop zelf.
        const estimatedMenuWidth = 190;
        let left = rect.right - estimatedMenuWidth;
        if (left < 8) {
          left = rect.left;
        }
        if (left + estimatedMenuWidth > window.innerWidth - 8) {
          left = window.innerWidth - estimatedMenuWidth - 8;
        }
        if (left < 8) {
          left = 8;
        }
        setMenuPos({
          top: rect.bottom + 6,
          left,
        });
      }
      return next;
    });
  };

  const handleSelect = (code) => {
    changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="language-switcher" ref={containerRef}>
      <button
        type="button"
        ref={btnRef}
        className="language-switcher-btn"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Taal / Langue / Language"
      >
        <span>{currentLabel}</span>
        <svg className="language-switcher-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && menuPos && (
        <ul
          className="language-switcher-menu"
          role="listbox"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
        >
          {LANGUAGES.map((lang) => (
            <li key={lang.code}>
              <button
                type="button"
                role="option"
                aria-selected={lang.code === current}
                className={`language-switcher-option ${lang.code === current ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                <span className="language-switcher-code">{lang.label}</span>
                <span className="language-switcher-name">{lang.name}</span>
                {lang.code === current && (
                  <svg className="language-switcher-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LanguageSwitcher;
